import React from "react";
import { CORECascader, CORECascaderProps } from "./CORECascader";
import { COREError } from "../Content/COREError";
import { CORELoading, CORELoadingProps } from "../Feedback/CORELoading";
import { Endpoint } from "./CORESelectAPI";
import { useAPIQuery } from "../../shared/customHoooks/useAPI";
import { DefaultOptionType } from "antd/lib/cascader";

type Value = (string | number)[];

export type CORECascaderAPIProps<ApiParam, APIResponse> = Omit<
  CORECascaderProps,
  ("options" & "onChange") | "value"
> & {
  onChange?: (newValue: Value) => void;
  value?: Value;
  endpoint: Endpoint;
  params?: ApiParam;
  transform: (arg: APIResponse) => CORECascaderProps["options"];
  skip?: boolean;
  loadingLayout?: CORELoadingProps["layout"];
};

const getArrayDepth = (arrays: Value | Value[0]): number =>
  Array.isArray(arrays) ? 1 + Math.max(0, ...arrays.map(getArrayDepth)) : 0;

const findLastValues = (arrays: Value) => {
  if (arrays === undefined) return [];
  if (Array.isArray(arrays)) {
    return arrays.map((array) =>
      Array.isArray(array) && getArrayDepth(arrays) >= 1
        ? array[array.length - 1]
        : array
    );
  }
  return [arrays];
};

const findLastChild = (
  option: DefaultOptionType | DefaultOptionType[]
): DefaultOptionType[] | DefaultOptionType => {
  if (!Array.isArray(option) && option.children) {
    const lastChild = option.children;
    return findLastChild(lastChild);
  }
  return option;
};

export const CORECascaderAPI = <ApiParam, APIResponse>({
  endpoint,
  params,
  transform,
  skip,
  value,
  loadingLayout,
  multiple,
  ...props
}: CORECascaderAPIProps<ApiParam, APIResponse>) => {
  const {
    sync,
    loading,
    error,
    data: { data },
  } = useAPIQuery(
    endpoint,
    {
      ...params,
    },
    skip
  );
  if (loading && !sync) return <CORELoading layout={loadingLayout} />;
  if (!sync) return null;
  if (error) return <COREError />;

  const options = !skip ? transform(data) : [];

  const matchingValue = () => {
    if (value === undefined) return undefined;
    if (!multiple) {
      return [value].filter(
        (item) => item?.length > 0 || (!Array.isArray(item) && item)
      );
    }
    if (value && Array.isArray(value)) {
      return (
        options
          ?.flatMap((option) =>
            option.value && value?.includes(option.value)
              ? [[option.value]].filter(Boolean)
              : findLastChild(option)
                  .filter(
                    (lastChild: DefaultOptionType) =>
                      lastChild.value && value?.includes(lastChild.value)
                  )
                  .map((lastChild: DefaultOptionType) => [
                    option.value,
                    lastChild.value,
                  ])
                  .filter(Boolean) || []
          )
          .filter(Boolean) || []
      );
    }
  };

  return (
    <CORECascader
      {...props}
      multiple={multiple}
      changeOnSelect
      value={matchingValue()}
      onChange={(val: Value) => {
        if (props.onChange === undefined) return;
        if (val === undefined) return props.onChange([]);
        return props.onChange(findLastValues(val));
      }}
      options={options}
    />
  );
};
